import { useState } from "react";

import { ApiError, api } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { canManageBilling } from "../lib/billing";
import { countyName } from "../lib/counties";
import { ADMIN_PATH, ALERTS_PATH } from "../lib/route";
import { Link } from "../router";

/**
 * `county` is the FIPS being looked at, or "" for everything held. It is named
 * here rather than assumed: a masthead reading "Miami-Dade County" above a
 * Palm Beach table is the kind of wrong that gets read as fact.
 */
export function TopBar({ county, newAlerts = 0 }:
                       { county: string; newAlerts?: number }) {
  const { user, signOut } = useAuth();
  const [openingPortal, setOpeningPortal] = useState(false);
  const [portalError, setPortalError] = useState("");
  const paid = user !== null && user.tier !== "free";
  const name = countyName(county);

  /**
   * Cancelling, changing a card and downloading an invoice all live in
   * Stripe's own portal — there is no billing UI here to keep in sync with
   * what Stripe believes.
   */
  async function manageBilling() {
    setPortalError("");
    setOpeningPortal(true);
    try {
      const { url } = await api.portal();
      window.location.href = url;
    } catch (cause) {
      // Never silent: a subscriber clicking "Billing" is usually trying to
      // stop being charged, and a dead button is the worst possible answer.
      setPortalError(
        cause instanceof ApiError ? cause.message : "Billing is unavailable.",
      );
      setOpeningPortal(false);
    }
  }

  return (
    <header className="topbar">
      {/* Home is the map, for members too — so the wordmark goes there. */}
      <Link to="/" className="wordmark">
        <span>D</span>is<span>TR</span>ess Signal
      </Link>
      <div className="loc hide-sm">
        {name ? `${name} County` : "All covered counties"}
      </div>
      <div className="spacer" />
      {portalError && <div className="topbar-error">{portalError}</div>}

      {/* Two groups, not one row of five.
          What was there before put three different kinds of thing at
          identical weight: places to go, a fact about you, and actions on your
          account. The role chip sat in the middle of the links — and being the
          only bordered element in the row, it had the strongest click
          affordance while being the one thing that is not clickable. */}
      <nav className="topnav">
        {user?.capabilities.can_watch && (
          <Link to={ALERTS_PATH} className="linkbtn">
            Alerts
            {/* The count is of rows that entered *our* dataset, which is why
                the page beside it says so rather than claiming a filing
                date. */}
            {newAlerts > 0 && <span className="dot">{newAlerts}</span>}
          </Link>
        )}
        {/* Only drawn for the admin role, but drawing it is not what makes the
            console private — every endpoint behind it answers 403 to anyone
            else. Hiding it just stops offering a page that would be refused. */}
        {user?.role === "admin" && (
          <Link to={ADMIN_PATH} className="linkbtn">
            Ops
          </Link>
        )}
      </nav>

      <div className="account">
        {/* Which account, not merely which role. Nothing in the product said
            this before, and it matters more here than in most: Terms §4.2
            sells to one named person, and `session_superseded` tells someone
            their credentials are in use somewhere they did not authorise —
            which is unanswerable if you cannot see who you are signed in as.
            First to be dropped on a narrow screen, like the county label. */}
        {user && <span className="who hide-sm">{user.email}</span>}
        {/* The role, not the tier: an administrator's tier reads "State", which
            is true of what they may do and wrong about who they are. */}
        {user && (
          <div className={`chip ${user.role === "admin" ? "admin" : paid ? "paid" : ""}`}>
            {user.role_name}
          </div>
        )}
        {canManageBilling(user) && (
          <button
            className="linkbtn"
            disabled={openingPortal}
            onClick={manageBilling}
          >
            {openingPortal ? "Opening…" : "Billing"}
          </button>
        )}
        <button className="linkbtn signout" onClick={signOut}>
          Sign out
        </button>
      </div>
    </header>
  );
}
